import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { OwnerOnly } from '@/lib/auth/OwnerOnly';
import { monthLabel, useStatementsList } from '@/lib/data/statements';
import { formatNad } from '@/lib/money/format';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/statements/')({
  component: StatementsPage,
});

function StatementsPage() {
  return (
    <OwnerOnly>
      <StatementsList />
    </OwnerOnly>
  );
}

function StatementsList() {
  const { supabase } = useAuth();
  const { businessId } = useProperty();
  const { data: statements, isLoading, error } = useStatementsList(supabase, businessId);

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Statements</h1>
        <p className="mt-sm text-body text-ink-2">
          A monthly record of the fees guests paid to NamibiaGo and the balances you recorded at the desk — generated
          on the 1st of each month for the month before.
        </p>
      </div>

      {isLoading ? <PageLoading /> : null}
      {error ? <p className="text-body text-danger">Couldn't load statements: {(error as Error).message}</p> : null}

      {!isLoading && !error && (!statements || statements.length === 0) ? (
        <p className="rounded-card border border-border bg-surface p-lg text-body text-ink-2">
          No statements yet — the first one generates after your first full calendar month of activity.
        </p>
      ) : null}

      {statements && statements.length > 0 ? (
        <div className="overflow-x-auto rounded-card border border-border bg-surface shadow-card">
          <table className="w-full min-w-[640px] text-body text-ink">
            <thead>
              <tr className="border-b border-hair text-left text-caption text-ink-2">
                <th className="px-md py-sm font-semibold">Period</th>
                <th className="px-md py-sm font-semibold">Bookings</th>
                <th className="px-md py-sm font-semibold">Fees guests paid to NamibiaGo</th>
                <th className="px-md py-sm font-semibold">Balances you recorded</th>
              </tr>
            </thead>
            <tbody>
              {statements.map((s) => (
                <tr key={s.id} className="border-b border-hair last:border-0 hover:bg-surface-2">
                  <td className="px-md py-sm">
                    <Link to="/statements/$period" params={{ period: s.period }} className="font-semibold text-canyon underline">
                      {monthLabel(s.period)}
                    </Link>
                  </td>
                  <td className="px-md py-sm">{s.bookings_count}</td>
                  <td className="px-md py-sm">{formatNad(s.fees_paid_nad)}</td>
                  <td className="px-md py-sm">{formatNad(s.balances_recorded_nad)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
